import Modal from "components/blocks/Modal";
import {useRef, useState} from "react";
import { useTranslation } from "react-i18next";
import useMutation from "hooks/useMutation";
import InternalRefereeTable from "./InternalRefereeTable";
import { UserResult } from "services/accountService/models/AccountType";
import {RefereesService} from "../../../../../../../../../services/RefereeService";
import {CreateCourseOrderRefereeRequest} from "../../../../../../../../../services/RefereeService/Model/RefereeTypes";
import RefereeDropDown from "./RefereeDropDown";
import ExternalRefereeSection from "./ExternalRefereeSection";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "components/blocks/DatePicker";

export type SelectedExaminerType = Pick<UserResult, "arabicName" | "englishName" | "id" | "email" | "college">;
type AddExaminerModalProps = {
  isOpen: boolean;
  onClose: (shouldUpdateTable?: boolean) => void;
  courseOrderEvaluationId: number;
};

const AddRefereeModal = ({ isOpen, onClose, courseOrderEvaluationId }: AddExaminerModalProps) => {
  const { t } = useTranslation(["Orders"]);

  const { control, register, setValue, getValues, formState: { errors } } = useForm<{ deadLine: Date | null; notes: string }>({
    defaultValues: {
      deadLine: new Date(new Date().setDate(new Date().getDate() + 1)),
      notes: ""
    }
  });

  const [selectedInternalExaminers, setSelectedInternalExaminers] = useState<SelectedExaminerType[]>([]);
  const [selectedExaminer, setSelectedExaminer] = useState<UserResult>();
  const [externalExaminersEmail, setExternalExaminersEmail] = useState<
    {
      email: string;
      isSentInvitation: boolean;
    }[]
  >([]);

  const { loading, mutateAsync } = useMutation({
    queryFn: async (request: CreateCourseOrderRefereeRequest[]) => {
      return await RefereesService.addReferee(request);
    },
  });

  const onAddExaminers = async () => {
    const examiners = selectedInternalExaminers.concat(externalExaminersEmail.map((u) => ({ email: u.email, id: "" })));

    const { deadLine, notes } = getValues();

    const newExaminers: CreateCourseOrderRefereeRequest[] = examiners.map((examiner) => ({
      refereeEmail: !examiner.id ? examiner.email : undefined,
      refereeId: examiner.id ? examiner.id : undefined,
      courseOrderEvaluationId: courseOrderEvaluationId,
      deadLine: deadLine as Date,
      notes: notes || "",
    }));

    const res = await mutateAsync(newExaminers);

    if (!res?.hasError) {
      onModalClose(true);
    }
  };

  const onModalClose = (shouldUpdateExaminerTable: boolean) => {
    onClose(shouldUpdateExaminerTable);
    setSelectedInternalExaminers([]);
    setExternalExaminersEmail([]);
  };

  const modalRef = useRef<HTMLDivElement | null>(null);

  return (
    <Modal
      title={t("addReferees")}
      isOpen={isOpen}
      onClose={() => onModalClose(false)}
      containerClassName="bg-white"
      size="lg"
      titleClassName="fs-6 text-dark"
    >
      <div ref={modalRef} className="d-flex flex-column gap-4">
        <RefereeDropDown
          selectedExaminer={selectedExaminer}
          setSelectedExaminer={setSelectedExaminer}
          externalExaminersEmail={externalExaminersEmail}
          setExternalExaminersEmail={setExternalExaminersEmail}
          selectedInternalExaminers={selectedInternalExaminers}
          setSelectedInternalExaminers={setSelectedInternalExaminers}
        />

        <ExternalRefereeSection
          externalExaminersEmail={externalExaminersEmail}
          setExternalExaminersEmail={setExternalExaminersEmail}
        />

        <InternalRefereeTable
          selectedInternalExaminers={selectedInternalExaminers}
          setSelectedInternalExaminers={setSelectedInternalExaminers}
        />

        <div  className="divider"></div>
        <Controller
          name="deadLine"
          control={control}
          render={({ field }) => (
            <DatePicker
              portalTarget={modalRef.current}
              label={t("deadLine")}
              labelClassName="fw-bold text-dark  "
              placeholder="../../...."
              wrapperClassName="w-50"
              value={field.value || new Date()}
              error={errors.deadLine?.message}
              minDate={new Date()}
              onChange={(date) => {
                if (date) {
                  setValue("deadLine", date);
                }
              }}
            />
          )}
        />

        <div>
          <label className="fw-bold text-dark mb-1">{t("notes")}</label>
          <textarea
            className="form-control"
            placeholder={t("notes")}
            {...register("notes")}
            rows={5}
            style={{ resize: "none" }}
          />
        </div>

        <div className="d-flex justify-content-center gap-4">
          <button className={`btn btn-outline-secondary border rounded-3 p-10-32P`} onClick={() => onModalClose(false)}>
            {t("Common:cancel")}
          </button>
          <button
            className="btn btn-primary p-10-32P rounded-3"
            onClick={onAddExaminers}
            disabled={
              loading ||
              !courseOrderEvaluationId ||
              (!selectedInternalExaminers.length && !externalExaminersEmail.length) ||
              !getValues().deadLine
            }
          >
            {!loading ? t("Common:add") : <div className="custom-loader"></div>}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddRefereeModal;
